import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  COACH_PERMISSION_KEYS,
  COACH_PERMISSION_LABELS,
  type CoachPermissions,
  useAffiliatesApi,
} from '@/hooks/useAffiliatesApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Crown,
  Loader2,
  Shield,
  ShieldCheck,
  Sparkles,
  User as UserIcon,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * FEAT-APR08-002 — Team Management page
 *
 * Owner-only hub for:
 *  - Listing all people in the gym (owners, coaches, members)
 *  - Showing current counts vs freemium quotas (owners 2, coaches 2, members 10)
 *  - Per-coach permission editor with Junior Coach / Gym Manager / Custom presets
 *  - Quick "Make Gym Manager" one-click for trusted coaches (Des 2026-04-08)
 */
export default function TeamManagement() {
  const affiliatesApi = useAffiliatesApi();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-management'],
    queryFn: async () => {
      const res = await affiliatesApi.getTeamManagement();
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: async ({
      memberId,
      update,
    }: {
      memberId: string;
      update: Partial<CoachPermissions> | { preset: 'junior_coach' | 'gym_manager' };
    }) => {
      const res = await affiliatesApi.updateCoachPermissions(memberId, update);
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-management'] });
      toast.success('Permissions updated');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update permissions');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
          <p className="text-destructive">Failed to load team</p>
        </CardContent>
      </Card>
    );
  }

  const { affiliate, people, quota } = data;
  const owners = people.filter((p) => p.member_role === 'owner');
  const coaches = people.filter((p) => p.member_role === 'coach');
  const members = people.filter((p) => p.member_role === 'member');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Team Management</h1>
        <p className="text-muted-foreground">
          {affiliate.name} — manage coaches, members, and who can do what in your gym.
        </p>
      </div>

      {/* Freemium quota panel (only visible when frozen) */}
      {quota.is_frozen && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              Freemium limits
            </CardTitle>
            <CardDescription>
              You're on the free plan. Subscribe to lift these limits.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <QuotaBar
              label="Owners / co-admins"
              count={quota.owners.count}
              cap={quota.owners.cap}
            />
            <QuotaBar label="Coaches" count={quota.coaches.count} cap={quota.coaches.cap} />
            <QuotaBar label="Members" count={quota.members.count} cap={quota.members.cap} />
          </CardContent>
        </Card>
      )}

      {/* Owners */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            Owners
            <Badge variant="outline" className="ml-2">
              {owners.length}
              {quota.owners.cap !== null ? ` / ${quota.owners.cap}` : ''}
            </Badge>
          </CardTitle>
          <CardDescription>Owners have full control of the gym.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {owners.length === 0 ? (
            <p className="text-sm text-muted-foreground">No owner rows beyond the primary owner.</p>
          ) : (
            owners.map((o) => (
              <div
                key={o.member_id}
                className="flex items-center justify-between rounded-md border border-border bg-card/50 p-3"
              >
                <div className="flex items-center gap-3">
                  <UserIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{o.name}</p>
                    <p className="text-xs text-muted-foreground">{o.email}</p>
                  </div>
                </div>
                <Badge>Owner</Badge>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Coaches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Coaches
            <Badge variant="outline" className="ml-2">
              {coaches.length}
              {quota.coaches.cap !== null ? ` / ${quota.coaches.cap}` : ''}
            </Badge>
          </CardTitle>
          <CardDescription>
            Coaches run day-to-day operations. Grant them permissions below — or set them to "Gym
            Manager" in one click for a fully-trusted coach.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {coaches.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No coaches yet. Invite a coach from the Team tab to get started.
            </p>
          ) : (
            coaches.map((c) => (
              <CoachRow
                key={c.member_id}
                coach={c}
                isPending={updatePermissionsMutation.isPending}
                onUpdate={(update) =>
                  updatePermissionsMutation.mutate({ memberId: c.member_id, update })
                }
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Members
            <Badge variant="outline" className="ml-2">
              {members.length}
              {quota.members.cap !== null ? ` / ${quota.members.cap}` : ''}
            </Badge>
          </CardTitle>
          <CardDescription>The athletes training at your gym.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {members.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet.</p>
          ) : (
            members.slice(0, 10).map((m) => (
              <div
                key={m.member_id}
                className="flex items-center justify-between rounded-md border border-border bg-card/50 p-3"
              >
                <div className="flex items-center gap-3">
                  <UserIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{m.email}</p>
                  </div>
                </div>
                <Badge variant="outline">Member</Badge>
              </div>
            ))
          )}
          {members.length > 10 && (
            <p className="text-xs text-muted-foreground mt-3">
              Showing first 10 of {members.length}. Use the Members page for the full list.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QuotaBar({
  label,
  count,
  cap,
}: {
  label: string;
  count: number;
  cap: number | null;
}) {
  if (cap === null) {
    return (
      <div>
        <p className="text-xs font-medium mb-1">{label}</p>
        <p className="text-sm text-muted-foreground">{count} — unlimited (paid)</p>
      </div>
    );
  }
  const pct = Math.min(100, (count / cap) * 100);
  const atCap = count >= cap;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-medium">{label}</p>
        <p
          className={`text-xs ${atCap ? 'text-destructive font-semibold' : 'text-muted-foreground'}`}
        >
          {count} / {cap}
        </p>
      </div>
      <div className="h-2 rounded-full bg-border overflow-hidden">
        <div
          className={`h-full transition-all ${atCap ? 'bg-destructive' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

type CoachPerson = {
  member_id: string;
  name: string;
  email: string;
  permissions: CoachPermissions | null;
};

function CoachRow({
  coach,
  isPending,
  onUpdate,
}: {
  coach: CoachPerson;
  isPending: boolean;
  onUpdate: (
    update: Partial<CoachPermissions> | { preset: 'junior_coach' | 'gym_manager' }
  ) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const perms = coach.permissions;
  const allFlagsOn = perms !== null && COACH_PERMISSION_KEYS.every((k) => perms[k] === true);

  return (
    <div className="rounded-md border border-border bg-card/50">
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-primary" />
          <div>
            <p className="font-medium flex items-center gap-2">
              {coach.name}
              {allFlagsOn && (
                <Badge className="text-xs" variant="default">
                  <Sparkles className="h-3 w-3 mr-1" />
                  Gym Manager
                </Badge>
              )}
            </p>
            <p className="text-xs text-muted-foreground">{coach.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            onClick={() => onUpdate({ preset: 'gym_manager' })}
          >
            Make Gym Manager
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={isPending}
            onClick={() => onUpdate({ preset: 'junior_coach' })}
          >
            Reset to Junior
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setExpanded((e) => !e)}>
            {expanded ? 'Hide' : 'Edit'}
          </Button>
        </div>
      </div>

      {expanded && perms && (
        <div className="border-t border-border p-3">
          <p className="text-xs text-muted-foreground mb-3">
            Tick the capabilities this coach can use. Owner-only actions (billing, gym settings,
            subscription) cannot be granted.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {COACH_PERMISSION_KEYS.map((key) => (
              <label
                key={key}
                className="flex items-center gap-2 cursor-pointer select-none text-sm"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-border accent-primary"
                  checked={perms[key]}
                  disabled={isPending}
                  onChange={(e) => onUpdate({ [key]: e.target.checked })}
                />
                <span>{COACH_PERMISSION_LABELS[key]}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
